import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useCrypto } from './crypto';
import { api } from '../api/client';
import { encrypt, decrypt } from '@mysecureroom/crypto';

export type DecisionStatus = 'pending' | 'decided' | 'deferred' | 'cancelled';
export type DecisionMode = 'pros-cons' | 'matrix';

export interface Decision {
  id: string;
  title: string;
  description: string | null;
  mode: DecisionMode;
  status: DecisionStatus;
  selected_option_id: string | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
  options?: DecisionOption[];
  criteria?: DecisionCriterion[];
}

export interface DecisionOption {
  id: string;
  decision_id: string;
  title: string;
  description: string | null;
  sort_order: number;
  total_score: number; // Calculated from scores
  pros?: string[];
  cons?: string[];
}

export interface DecisionCriterion {
  id: string;
  decision_id: string;
  title: string;
  weight: number; // 1-10
  sort_order: number;
}

export interface DecisionScore {
  id: string;
  decision_id: string;
  option_id: string;
  criterion_id: string;
  score: number; // 1-10
}

interface DecisionsState {
  decisions: Decision[];
  loading: boolean;
  error: string | null;
  initialized: boolean;
}

interface DecisionsContextValue extends DecisionsState {
  initialize: () => Promise<void>;
  createDecision: (input: { title: string; mode: DecisionMode; description?: string }) => Promise<Decision>;
  updateDecision: (id: string, updates: {
    title?: string;
    description?: string | null;
    status?: DecisionStatus;
    selected_option_id?: string | null;
  }) => Promise<void>;
  deleteDecision: (id: string) => Promise<void>;
  addOption: (decisionId: string, title: string, description?: string) => Promise<DecisionOption>;
  updateOption: (decisionId: string, optionId: string, updates: { title?: string; description?: string | null }) => Promise<void>;
  deleteOption: (decisionId: string, optionId: string) => Promise<void>;
  addCriterion: (decisionId: string, title: string, weight: number) => Promise<DecisionCriterion>;
  updateCriterion: (decisionId: string, criterionId: string, updates: { title?: string; weight?: number }) => Promise<void>;
  deleteCriterion: (decisionId: string, criterionId: string) => Promise<void>;
  scoreOption: (decisionId: string, optionId: string, criterionId: string, score: number) => Promise<void>;
  addProCon: (decisionId: string, optionId: string, type: 'pro' | 'con', text: string) => Promise<void>;
  removeProCon: (decisionId: string, optionId: string, type: 'pro' | 'con', index: number) => Promise<void>;
  loadDetails: (decisionId: string) => Promise<void>;
  clear: () => void;
}

const DecisionsContext = createContext<DecisionsContextValue | null>(null);

export function DecisionsProvider({ children }: { children: ReactNode }) {
  const crypto = useCrypto();
  const [state, setState] = useState<DecisionsState>({
    decisions: [],
    loading: false,
    error: null,
    initialized: false,
  });

  const encryptText = async (text: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return encrypt(crypto.encryptionKey, text);
  };

  const decryptText = async (encrypted: string): Promise<string> => {
    if (!crypto.encryptionKey) throw new Error('Encryption key not available');
    return decrypt(crypto.encryptionKey, encrypted);
  };

  const initialize = async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const result = await api.get<{ decisions: Decision[] }>('/decisions');

      const decryptedDecisions = await Promise.all(
        result.decisions.map(async (d) => ({
          ...d,
          title: await decryptText(d.title),
          description: d.description ? await decryptText(d.description) : null,
        }))
      );

      setState({
        decisions: decryptedDecisions,
        loading: false,
        error: null,
        initialized: true,
      });
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load decisions',
      }));
    }
  };

  const createDecision = async (input: { title: string; mode: DecisionMode; description?: string }): Promise<Decision> => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedTitle = await encryptText(input.title);
      const encryptedDescription = input.description ? await encryptText(input.description) : undefined;

      const result = await api.post<{ decision: Decision }>('/decisions', {
        title: encryptedTitle,
        description: encryptedDescription,
        mode: input.mode,
      });

      const decryptedDecision = {
        ...result.decision,
        title: await decryptText(result.decision.title),
        description: result.decision.description ? await decryptText(result.decision.description) : null,
      };

      setState(s => ({
        ...s,
        decisions: [...s.decisions, decryptedDecision],
        loading: false,
      }));

      return decryptedDecision;
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to create decision',
      }));
      throw error;
    }
  };

  const updateDecision = async (id: string, updates: {
    title?: string;
    description?: string | null;
    status?: DecisionStatus;
    selected_option_id?: string | null;
  }) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      const encryptedUpdates: any = { ...updates };
      if (updates.title) encryptedUpdates.title = await encryptText(updates.title);
      if (updates.description) encryptedUpdates.description = await encryptText(updates.description);

      const result = await api.patch<{ decision: Decision }>(`/decisions/${id}`, encryptedUpdates);

      const decryptedDecision = {
        ...result.decision,
        title: await decryptText(result.decision.title),
        description: result.decision.description ? await decryptText(result.decision.description) : null,
      };

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d => d.id === id ? decryptedDecision : d),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to update decision',
      }));
    }
  };

  const deleteDecision = async (id: string) => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      await api.delete(`/decisions/${id}`);
      setState(s => ({
        ...s,
        decisions: s.decisions.filter(d => d.id !== id),
        loading: false,
      }));
    } catch (error) {
      setState(s => ({
        ...s,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to delete decision',
      }));
    }
  };

  const loadDetails = async (decisionId: string) => {
    try {
      const result = await api.get<{
        decision: Decision;
        options: DecisionOption[];
        criteria: DecisionCriterion[];
      }>(`/decisions/${decisionId}/details`);

      const decryptedOptions = await Promise.all(
        result.options.map(async (o) => ({
          ...o,
          title: await decryptText(o.title),
          description: o.description ? await decryptText(o.description) : null,
        }))
      );

      const decryptedCriteria = await Promise.all(
        result.criteria.map(async (c) => ({
          ...c,
          title: await decryptText(c.title),
        }))
      );

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId
            ? { ...d, options: decryptedOptions, criteria: decryptedCriteria }
            : d
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const addOption = async (decisionId: string, title: string, description?: string): Promise<DecisionOption> => {
    try {
      const encryptedTitle = await encryptText(title);
      const encryptedDescription = description ? await encryptText(description) : undefined;

      const result = await api.post<{ option: DecisionOption }>(`/decisions/${decisionId}/options`, {
        title: encryptedTitle,
        description: encryptedDescription,
      });

      const decryptedOption = {
        ...result.option,
        title: await decryptText(result.option.title),
        description: result.option.description ? await decryptText(result.option.description) : null,
      };

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.options
            ? { ...d, options: [...d.options, decryptedOption] }
            : d
        ),
      }));

      return decryptedOption;
    } catch (error) {
      throw error;
    }
  };

  const updateOption = async (decisionId: string, optionId: string, updates: { title?: string; description?: string | null }) => {
    try {
      const encryptedUpdates: any = {};
      if (updates.title) encryptedUpdates.title = await encryptText(updates.title);
      if (updates.description) encryptedUpdates.description = await encryptText(updates.description);

      const result = await api.patch<{ option: DecisionOption }>(`/decisions/${decisionId}/options/${optionId}`, encryptedUpdates);

      const decryptedOption = {
        ...result.option,
        title: await decryptText(result.option.title),
        description: result.option.description ? await decryptText(result.option.description) : null,
      };

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.options
            ? { ...d, options: d.options.map(o => o.id === optionId ? decryptedOption : o) }
            : d
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const deleteOption = async (decisionId: string, optionId: string) => {
    try {
      await api.delete(`/decisions/${decisionId}/options/${optionId}`);

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.options
            ? { ...d, options: d.options.filter(o => o.id !== optionId) }
            : d
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const addCriterion = async (decisionId: string, title: string, weight: number): Promise<DecisionCriterion> => {
    try {
      const encryptedTitle = await encryptText(title);

      const result = await api.post<{ criterion: DecisionCriterion }>(`/decisions/${decisionId}/criteria`, {
        title: encryptedTitle,
        weight,
      });

      const decryptedCriterion = {
        ...result.criterion,
        title: await decryptText(result.criterion.title),
      };

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.criteria
            ? { ...d, criteria: [...d.criteria, decryptedCriterion] }
            : d
        ),
      }));

      return decryptedCriterion;
    } catch (error) {
      throw error;
    }
  };

  const updateCriterion = async (decisionId: string, criterionId: string, updates: { title?: string; weight?: number }) => {
    try {
      const encryptedUpdates: any = { ...updates };
      if (updates.title) encryptedUpdates.title = await encryptText(updates.title);

      const result = await api.patch<{ criterion: DecisionCriterion }>(`/decisions/${decisionId}/criteria/${criterionId}`, encryptedUpdates);

      const decryptedCriterion = {
        ...result.criterion,
        title: await decryptText(result.criterion.title),
      };

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.criteria
            ? { ...d, criteria: d.criteria.map(c => c.id === criterionId ? decryptedCriterion : c) }
            : d
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const deleteCriterion = async (decisionId: string, criterionId: string) => {
    try {
      await api.delete(`/decisions/${decisionId}/criteria/${criterionId}`);

      setState(s => ({
        ...s,
        decisions: s.decisions.map(d =>
          d.id === decisionId && d.criteria
            ? { ...d, criteria: d.criteria.filter(c => c.id !== criterionId) }
            : d
        ),
      }));
    } catch (error) {
      throw error;
    }
  };

  const scoreOption = async (decisionId: string, optionId: string, criterionId: string, score: number) => {
    try {
      await api.post(`/decisions/${decisionId}/scores`, {
        option_id: optionId,
        criterion_id: criterionId,
        score,
      });

      // Refresh decision details to get updated scores
      await loadDetails(decisionId);
    } catch (error) {
      throw error;
    }
  };

  const addProCon = async (decisionId: string, optionId: string, type: 'pro' | 'con', text: string) => {
    try {
      const encryptedText = await encryptText(text);
      await api.post(`/decisions/${decisionId}/options/${optionId}/procons`, {
        type,
        text: encryptedText,
      });

      // Refresh to get updated pros/cons
      await loadDetails(decisionId);
    } catch (error) {
      throw error;
    }
  };

  const removeProCon = async (decisionId: string, optionId: string, type: 'pro' | 'con', index: number) => {
    try {
      await api.delete(`/decisions/${decisionId}/options/${optionId}/procons/${type}/${index}`);

      // Refresh to get updated pros/cons
      await loadDetails(decisionId);
    } catch (error) {
      throw error;
    }
  };

  const clear = () => {
    setState({
      decisions: [],
      loading: false,
      error: null,
      initialized: false,
    });
  };

  const value: DecisionsContextValue = {
    ...state,
    initialize,
    createDecision,
    updateDecision,
    deleteDecision,
    addOption,
    updateOption,
    deleteOption,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    scoreOption,
    addProCon,
    removeProCon,
    loadDetails,
    clear,
  };

  return <DecisionsContext.Provider value={value}>{children}</DecisionsContext.Provider>;
}

export function useDecisions() {
  const context = useContext(DecisionsContext);
  if (!context) {
    throw new Error('useDecisions must be used within DecisionsProvider');
  }
  return context;
}
